import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import AdfImage from "./AdfImage";
import { mediaUrl } from "../../lib/api";
import { pickLocale } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { adfAssets } from "./assets";

/**
 * The homepage hero, in the reference design.
 *
 * Full-bleed photograph on a charcoal ground, the copy centred over it: an
 * eyebrow, a large serif headline, a line of support, then a solid and an
 * outlined button side by side. `tracking-[0.1em]` is written out wherever the
 * reference says `tracking-widest`, because this project overrides that scale
 * to 0.24em site-wide and the reference was drawn against Tailwind's default.
 *
 * It stays a sequence an editor controls. One slide renders exactly as the
 * reference does and pays for no carousel; the arrows and dots only appear once
 * there is something to move between.
 */
const INTERVAL = 6500;

/** The video id out of any YouTube link shape the editor might paste. */
function youtubeId(url) {
  const match = String(url || "").match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{6,20})/,
  );
  return match ? match[1] : "";
}

export default function HeroSlider({ slides, fallback }) {
  const { locale, t } = useI18n();
  const rows = slides?.length ? slides : [fallback];
  const many = rows.length > 1;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  // YouTube fon videosu: iframe yüklənən kimi qara fon çəkir, ona görə
  // thumbnail iframe-in ÜSTÜNDƏ durur və yalnız player həqiqətən "playing"
  // siqnalı verəndə əriyib gedir.
  const [ytPlaying, setYtPlaying] = useState(false);
  const ytFade = useRef(null);

  const go = useCallback((next) => {
    setIndex(((next % rows.length) + rows.length) % rows.length);
  }, [rows.length]);

  useEffect(() => { if (index >= rows.length) setIndex(0); }, [rows.length, index]);

  useEffect(() => {
    // Slayd dəyişəndə növbəti video öz posterindən başlayır.
    setYtPlaying(false);
    return () => clearTimeout(ytFade.current);
  }, [index]);

  useEffect(() => {
    // enablejsapi=1 olan embed "listening" salamından sonra vəziyyətini
    // postMessage ilə göndərir — API skripti yükləmədən qulaq asırıq.
    const onMessage = (event) => {
      if (!/^https:\/\/www\.youtube(-nocookie)?\.com$/.test(event.origin)) return;
      let data = event.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      const state = data?.event === "infoDelivery" ? data.info?.playerState
        : data?.event === "onStateChange" ? data.info : undefined;
      if (state === 1) setYtPlaying(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const onYtLoad = useCallback((event) => {
    try {
      event.currentTarget.contentWindow?.postMessage(
        JSON.stringify({ event: "listening", id: "adf-hero", channel: "widget" }),
        "*",
      );
    } catch { /* cross-origin qəza salmasın */ }
    // Siqnal heç gəlməsə belə poster əbədi qalmasın — YouTube-un öz ekranı
    // donmuş şəkildən faydalıdır.
    clearTimeout(ytFade.current);
    ytFade.current = setTimeout(() => setYtPlaying(true), 8000);
  }, []);

  useEffect(() => {
    if (!many || paused) return undefined;
    // Anyone who has asked the system to reduce motion is not asking for a
    // carousel that advances on its own.
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (still) return undefined;
    timer.current = setTimeout(() => go(index + 1), INTERVAL);
    return () => clearTimeout(timer.current);
  }, [index, many, paused, go]);

  const slide = rows[Math.min(index, rows.length - 1)] || {};
  const label = (value) => pickLocale(value, locale) || "";

  const primaryLabel = label(slide.cta_label);
  const secondaryLabel = label(slide.cta2_label);
  // Video slaydında düymələr filmi örtməsin: aşağıda, xətlərin üstündə,
  // balaca və incə haşiyəli; dolu ağ görünüş yalnız hover-dadır.
  const videoSlide = Boolean(slide.video_url || youtubeId(slide.youtube_url) || (!slide.image_url && fallback?.video_url));

  const ctaButtons = (primaryLabel || secondaryLabel) && (
    <div className={`pointer-events-auto flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto ${videoSlide ? "sm:gap-3" : ""}`}>
      {slide.cta_link && primaryLabel && (
        <Link
          to={slide.cta_link}
          className={videoSlide
            ? "w-full sm:w-auto border border-white/60 text-white bg-transparent px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.1em] hover:bg-white hover:text-charcoal hover:border-white transition-colors duration-300 text-center rounded-none"
            : "w-full sm:w-auto bg-white text-charcoal px-10 py-4 text-xs font-semibold uppercase tracking-[0.1em] hover:bg-charcoal hover:text-white transition-colors duration-300 text-center rounded-none"}
        >
          {primaryLabel}
        </Link>
      )}
      {slide.cta2_link && secondaryLabel && (
        <Link
          to={slide.cta2_link}
          className={`w-full sm:w-auto border text-white text-center flex items-center justify-center gap-2 rounded-none font-semibold uppercase tracking-[0.1em] hover:bg-white hover:text-charcoal transition-colors duration-300 ${
            videoSlide ? "border-white/60 px-8 py-3 text-[11px]" : "border-white px-10 py-4 text-xs"
          }`}
        >
          {secondaryLabel}
        </Link>
      )}
    </div>
  );

  return (
    <section
      className="relative h-[85dvh] lg:h-[95dvh] w-full flex items-center justify-center overflow-hidden bg-charcoal"
      data-testid="home-hero"
      aria-roledescription={many ? "carousel" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="absolute inset-0 z-0">
        {rows.map((row, position) => {
          // Motion wins when the editor set it. Videos mount only while their
          // slide is showing — an unmounted <video>/iframe cannot keep playing
          // (or keep making sound) behind the next slide.
          const ytId = youtubeId(row.youtube_url);
          const videoSource = row.video_url || (!ytId && fallback?.video_url);
          const auto = row.video_autoplay !== false;
          // Browsers refuse un-muted autoplay outright, so autoplay implies
          // muted; sound is only possible when the visitor presses play.
          const muted = row.video_muted !== false || auto;
          const loop = row.video_loop !== false;
          const contain = row.video_fit === "contain";
          // Zoom faizlə: 100-dən yuxarı hər şey kənarları kəsir. YouTube-un
          // loqo/başlıq elementləri embed-də söndürülə bilmir — onları kadrdan
          // kənara yalnız böyütmə itələyir.
          const zoom = Math.max(100, Math.min(250, Number(row.video_zoom) || 100)) / 100;
          // Yalnız string URL-lər: fallback.image_url konfiqsiz saytda bundled
          // asset OBYEKTİ ola bilir — mediaUrl() onu yeyə bilmir.
          const poster = [row.poster_url, fallback?.poster_url, row.image_url, fallback?.image_url]
            .find((value) => typeof value === "string" && value);

          if (ytId && position === index) {
            const params = new URLSearchParams({
              autoplay: auto ? "1" : "0",
              mute: muted ? "1" : "0",
              controls: auto ? "0" : "1",
              playsinline: "1",
              rel: "0",
              iv_load_policy: "3",
              disablekb: "1",
              fs: "0",
              enablejsapi: "1",
              origin: window.location.origin,
              ...(loop ? { loop: "1", playlist: ytId } : {}),
            });
            return (
              <div
                key={position}
                className={`adf-hero-yt ${contain ? "adf-hero-yt--contain" : ""}`}
                // A background film should not swallow the clicks meant for
                // the arrows and buttons; a click-to-play one must receive them.
                style={{ pointerEvents: auto ? "none" : "auto" }}
              >
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${ytId}?${params.toString()}`}
                  title=""
                  style={{ transform: `translate(-50%, -50%) scale(${zoom})` }}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  onLoad={onYtLoad}
                  aria-hidden={auto ? "true" : undefined}
                />
                {/* Thumbnail iframe-in ÜSTÜNDƏ durur — iframe yüklənən kimi
                    qara fon çəkib altdakı şəkli örtür, ona görə poster yalnız
                    player "playing" deyəndə əriyir. Play-düyməli rejimdə isə
                    YouTube öz thumbnail-ını göstərir, bizimki lazım deyil. */}
                {auto && poster && (
                  <img
                    src={mediaUrl(poster)}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{
                      opacity: ytPlaying ? 0 : 1,
                      transition: "opacity 700ms ease",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </div>
            );
          }
          if (videoSource && position === index) {
            return (
              <video
                key={position}
                className={`adf-hero-image absolute inset-0 w-full h-full ${contain ? "object-contain" : "object-cover"}`}
                style={{
                  opacity: 0.9,
                  pointerEvents: auto ? "none" : "auto",
                  transform: zoom > 1 ? `scale(${zoom})` : undefined,
                  transformOrigin: "center",
                }}
                src={mediaUrl(videoSource)}
                poster={poster ? mediaUrl(poster) : undefined}
                autoPlay={auto}
                muted={muted}
                loop={loop}
                controls={!auto}
                playsInline
                preload="metadata"
              />
            );
          }
          if ((ytId || videoSource) && position !== index) {
            // The inactive video slide holds its poster so the crossfade has
            // something to fade from when the carousel returns to it.
            const still = row.image_url || fallback?.image_url;
            if (!still) return null;
            return (
              <AdfImage
                key={position}
                source={still}
                mobileSource={row.mobile_image_url || undefined}
                fallback={adfAssets.hero}
                alt=""
                sizes="100vw"
                className="adf-hero-image absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: 0 }}
                aria-hidden="true"
              />
            );
          }
          const source = row.image_url || fallback?.image_url;
          // Nothing known yet. The ground and its gradient are the design's own
          // backdrop, so the hero simply holds until there is a real photograph
          // — rather than borrowing a bundled one and swapping it out.
          if (!source) return null;
          return (
          <AdfImage
            /* Keyed by position, not by URL. Keying by URL remounted the
               element every time the image changed — so when the cached config
               was replaced by a newly saved one, the hero blanked between the
               two rather than crossing over. The browser holds the painted
               frame until the new source has decoded. */
            key={position}
            source={source}
            mobileSource={row.mobile_image_url || undefined}
            fallback={adfAssets.hero}
            alt=""
            sizes="100vw"
            priority={position === 0}
            className="adf-hero-image absolute inset-0 w-full h-full object-cover opacity-90 transition-opacity duration-700"
            /* The crop the editor chose, per viewport. One element serves both
               sources, so the two positions travel as custom properties and a
               media query picks between them — `object-top` used to decide for
               everyone, which cost a portrait phone about two thirds of a wide
               frame with no say in which two thirds. */
            style={{
              opacity: position === index ? 0.9 : 0,
              "--hero-focal": row.focal_point || "50% 50%",
              "--hero-focal-mobile": row.mobile_focal_point || row.focal_point || "50% 50%",
            }}
            aria-hidden={position === index ? undefined : "true"}
          />
          );
        })}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-charcoal/40 via-transparent to-charcoal/30" />
      </div>

      {/* pointer-events-none: mətn qatı klik udmasın — play düyməli video
          slaydlarda oynatmaq mümkün olsun; keçidlər aşağıda özləri auto edir. */}
      <div
        className="pointer-events-none relative z-10 text-center px-6 max-w-4xl mx-auto flex flex-col items-center mt-20 animate-fade-up"
        key={index}
      >
        {label(slide.eyebrow) && (
          <p className="w-full text-white/90 uppercase tracking-[0.2em] text-[10px] sm:text-xs mb-6 font-medium">
            {label(slide.eyebrow)}
          </p>
        )}
        <h1 className="w-full max-w-full text-4xl sm:text-6xl md:text-8xl text-white font-serif mb-6 leading-[1.1] tracking-tight">
          {label(slide.title)}
        </h1>
        {label(slide.subtitle) && (
          <p className="text-white/90 text-sm md:text-base font-light mb-10 max-w-md">
            {label(slide.subtitle)}
          </p>
        )}
        {!videoSlide && ctaButtons}
      </div>

      {/* Video slaydında düymələr aşağıda — slayd xətlərinin dərhal üstündə. */}
      {videoSlide && ctaButtons && (
        <div className="pointer-events-none absolute inset-x-0 bottom-16 z-10 flex justify-center px-6">
          {ctaButtons}
        </div>
      )}

      {many && (
        <>
          <button
            type="button"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white transition-colors"
            onClick={() => go(index - 1)}
            aria-label={t("hero.previous", "Əvvəlki")}
            data-testid="hero-prev"
          >
            <ChevronLeft size={26} strokeWidth={1.2} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white transition-colors"
            onClick={() => go(index + 1)}
            aria-label={t("hero.next", "Növbəti")}
            data-testid="hero-next"
          >
            <ChevronRight size={26} strokeWidth={1.2} aria-hidden="true" />
          </button>
          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2"
            role="tablist"
            aria-label={t("hero.slides", "Slaydlar")}
          >
            {rows.map((row, position) => (
              <button
                key={`dot-${row.image_url || position}`}
                type="button"
                role="tab"
                aria-selected={position === index}
                aria-label={`${position + 1} / ${rows.length}`}
                className={`h-[3px] w-8 transition-colors ${position === index ? "bg-white" : "bg-white/35"}`}
                onClick={() => go(position)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
